'use client'

import { useTranslations } from 'next-intl'
import { type CSSProperties } from 'react'
import { toast } from 'react-toastify'

import { copy } from '@/shared/lib'
import { Icon } from '@/shared/ui'

type Props = {
  title: string
  url?: string
  text?: string
  label?: string
  successMessage?: string
  onShared?: () => void
  className?: string
  style?: CSSProperties
}

export const ShareButton = ({
  title,
  url: urlProp,
  text,
  label = '공유하기',
  successMessage: successMessageProp,
  onShared,
  className,
  style,
}: Props) => {
  const t = useTranslations('common')

  const successMessage = successMessageProp ?? t('toast.link_copied')

  const copyUrl = async (url: string) => {
    const { success } = await copy(url)

    if (success) {
      toast(successMessage)
    }

    return success
  }

  const handleClick = async () => {
    const url =
      process.env.NEXT_PUBLIC_ENVIRONMENT !== 'local' && urlProp
        ? urlProp
        : window.location.href

    if (!navigator.share) {
      const success = await copyUrl(url)

      if (success) onShared?.()

      return
    }

    try {
      const defaultText = t('share.text', { title, url })

      await navigator.share({
        text: text ?? defaultText,
      })

      onShared?.()
    } catch (err) {
      // 사용자가 그냥 닫은 경우 (AbortError) → 무시
      const isUserCancelled = err instanceof Error && err.name === 'AbortError'

      if (isUserCancelled) return

      const success = await copyUrl(url)

      if (success) onShared?.()
    }
  }

  return (
    <button
      type="button"
      aria-label={label}
      className={className}
      style={style}
      onClick={handleClick}
    >
      <Icon name="share" size={24} strokeWidth={1.5} />
      <span className="sr-only">{label}</span>
    </button>
  )
}
